import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  FormControlLabel,
  Grid2,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import { AddItem, ProductField, UpdateItemProductRootOnBasket } from "../../../Redux/Reducers/redux-slice-basket";
import FormSection from "../../Forms/FormSection/form-section";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import ConfigFormIcon from "../ConfigFormIcon";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { DeliveryMethod } from "../../../Core/Api/Types/delivery-method";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetFileSize, IsEmail, IsListOfEmails } from "../../../Utils/helpers";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useDispatch } from "react-redux";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { mediaService, userService } from "../../../Core/Api/services";
import { DbFileInfos } from "../../../Core/Api/Types/db-file-infos";
import { FileCategory } from "../../../Core/Api/Types/file-category";
import DataTable, { CellSize } from "../../Table/table";
import { GridColDef } from "@mui/x-data-grid";
import { throttle } from "throttle-debounce";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { RecentEmailContext } from "../../../Core/Api/Types/get-recent-emails-response";
import { useTranslation } from "react-i18next";
import { AddItemToBasketRequest } from "../../../Core/Api/Types/add-item-to-basket-request";
import { useAppDispatch } from "../../../Redux/redux-store";

interface ConfigCommonFormDeliveryProps {
  item: DbOrderBasketItemInfos;
  formid: string;
  storages: DbStorageInfos[];
  maillists: MailingListDto[];
}

const ConfigCommonSectionFormDelivery: React.FC<ConfigCommonFormDeliveryProps> = (props) => {
  const { t } = useTranslation();
  const { item, formid, storages, maillists } = props;
  const [ml, setMl] = useState<MailingListDto>();
  const [files, setFiles] = useState<DbFileInfos[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<number[]>(item.selectedProduct?.transferMediaAnnexFileIds ?? []);
  const [fetchedUsersEmails, setFetchedUsersEmails] = useState<string[]>([]);
  const [query, setQuery] = useState<string>("");
  const [recentEmails, setRecentEmails] = useState<string[]>([]);

  const deliveryMethodOptions: DeliveryMethod[] = useMemo(() => {
    const output =
      item.selectedProduct?.type !== OrderProductType.DcpKdmGeneration
        ? [DeliveryMethod.AsperaFaspex, DeliveryMethod.UNC]
        : [DeliveryMethod.AsperaFaspex, DeliveryMethod.UNC, DeliveryMethod.MailAttachment];
    if (
      (item.fileTransferSize != null && item.fileTransferSize < GlobalConst.MAX_FILE_SIZE_HTTP_DOWNLOAD) ||
      item.objectLayer === ObjectLayer.Clip ||
      item.selectedProduct?.proxy
    )
      output.push(DeliveryMethod.Http);
    return output;
  }, [item]);

  const dispatch = useAppDispatch();

  // Form validator
  const formSectionDelivery = useAppSelector(GetSectionIsValid(formid, "sectionDelivery"));

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    setQuery("");
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("table:headerName.fileName"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      disableColumnMenu: true,
      renderCell: (data: any) => (
        <Typography variant="body2" pt={1} pb={1}>
          {data.value}
        </Typography>
      ),
    },
    {
      field: "sizeInBytes",
      headerName: t("common:size"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      disableColumnMenu: true,
      valueGetter: (value) => GetFileSize(parseInt(value)),
    },
  ];

  useEffect(() => {
    handleUpdateProductRoot(ProductField.TransferMediaAnnexFileIds, selectedFiles);
  }, [selectedFiles]);

  useEffect(() => {
    if (item.objectId && item.selectedProduct) {
      if (item.selectedProduct.type === OrderProductType.FaspexSend)
        mediaService.GetFilesByObjectId(item.objectId).then((response) => {
          if (response.files) setFiles(response.files.filter((x) => x.category === FileCategory.Linked));
        });
      else if (
        [
          OrderProductType.TranscodeGeneric,
          OrderProductType.TranscodeByProfile,
          OrderProductType.TranscodeBundleByProfile,
        ].includes(item.selectedProduct.type!)
      ) {
        Promise.all([
          mediaService.GetFilesByObjectId(item.objectId),
          mediaService.GetFilesByObjectId(item.selectedContent!.id),
        ]).then(([titleFiles, contentFiles]) => {
          let mediaFiles: DbFileInfos[] = [];
          if (titleFiles.files)
            mediaFiles = mediaFiles.concat(titleFiles.files.filter((x) => x.category === FileCategory.Linked));
          if (contentFiles.files)
            mediaFiles = mediaFiles.concat(contentFiles.files.filter((x) => x.category === FileCategory.Linked));

          setFiles(mediaFiles);
        });
      }
    }
  }, [item.objectId, item.selectedProduct]);

  useEffect(() => {
    handleUpdateProductRoot(
      ProductField.DeliveryMethod,
      item.selectedProduct!.deliveryMethod ??
        (item.selectedProduct?.deliveryStorageId ? DeliveryMethod.UNC : DeliveryMethod.AsperaFaspex)
    );
  }, []);

  const handleAutocompleteUsersMailInputChange = (searchValue: string) => {
    setQuery(searchValue);

    if (!searchValue || searchValue === "") return;

    throttledUpdateSearchEmail(searchValue);
  };

  const throttledUpdateSearchEmail = useMemo(
    () =>
      throttle(
        GlobalConst.SEARCH_THROTTLE_DELAY_MS,
        (searchValue: string) => {
          userService.GetUsersEmailsFromSearh(searchValue).then((emails) => {
            setFetchedUsersEmails(emails);
          });
        },
        {
          noLeading: true,
        }
      ),
    []
  );

  const statusText = useMemo(() => {
    const contents: string[] = [];

    // Delivery method
    switch (item.selectedProduct!.deliveryMethod) {
      case DeliveryMethod.AsperaFaspex:
        contents.push(t("form:statusText.asperaDownloadLink"));
        break;
      case DeliveryMethod.Http:
        contents.push("HTTP");
        break;
      case DeliveryMethod.UNC:
        contents.push(t("form:statusText.transfer"));
    }

    if (item.selectedProduct!.deliveryStorageId) {
      const storageName = storages.find((x) => x.id === item.selectedProduct!.deliveryStorageId)?.name!;
      if (storageName) contents.push(t("form:statusText.toName", { name: storageName }));
    }

    if (item.selectedProduct!.emailNotificationList && item.selectedProduct!.emailNotificationList.length > 0) {
      if (item.selectedProduct!.emailNotificationList.length > 1)
        contents.push(t("form:statusText.notifyEmails", { count: item.selectedProduct!.emailNotificationList.length }));
      else contents.push(t("form:statusText.notifyEmail", { email: item.selectedProduct!.emailNotificationList[0] }));
    }

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    return t("common:configurationRequired");
  }, [formSectionDelivery, item, storages]);

  useEffect(() => {
    userService.GetRecentEmails(RecentEmailContext.Basket).then((response) => {
      if (response.emails) setRecentEmails(response.emails);
    });
  }, []);

  const dcpOriginalVersionPackageId: number | undefined = useMemo(() => {
    if (item.assetInfos?.type !== ObjectType.DcpPackage) return undefined;

    if (!item.selectedProduct?.settings?.dcpPackageInfos?.ovMasterDcpPackageId) return undefined;

    return item.selectedProduct?.settings?.dcpPackageInfos?.ovMasterDcpPackageId;
  }, [item.assetInfos?.type, item.selectedProduct?.settings?.dcpPackageInfos?.ovMasterDcpPackageId]);

  const addOriginalDcpVersionPackage = useCallback(() => {
    if (!dcpOriginalVersionPackageId) return;

    let item: AddItemToBasketRequest = {
      objectId: dcpOriginalVersionPackageId,
      episodesNbList: null,
      externalContractId: null,
    };
    dispatch(AddItem(item));
  }, [dcpOriginalVersionPackageId]);

  return (
    <>
      <FormSection
        icon={<ConfigFormIcon icon="delivery" />}
        title={t("common:delivery")}
        status={formSectionDelivery ? "complete" : "warning"}
        statusText={statusText}
      >
        <Grid2 container spacing={3}>
          {item.selectedProduct?.type === OrderProductType.FaspexSend &&
            (item.assetInfos?.type === ObjectType.Video || item.assetInfos?.type === ObjectType.Audio) && (
              <Grid2 size={12}>
                {/* Option to delivey low res proxy file */}
                <>
                  <Typography color="text.primary">
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={item.selectedProduct?.proxy}
                          onChange={(v) => {
                            handleUpdateProductRoot(ProductField.Proxy, null);

                            if (v.target.checked)
                              handleUpdateProductRoot(ProductField.DeliveryMethod, DeliveryMethod.Http);
                          }}
                        />
                      }
                      label={t("field:label.deliverProxyFile")}
                    />
                  </Typography>
                </>
                <Divider sx={{ mt: 2 }} />
              </Grid2>
            )}
          <Grid2 size={5}>
            <FormAutocomplete
              label={t("field:label.deliveryMode")}
              options={deliveryMethodOptions}
              value={item.selectedProduct!.deliveryMethod}
              multiple={false}
              onChange={(v) => {
                handleUpdateProductRoot(ProductField.DeliveryMethod, v);

                if (!v || v !== DeliveryMethod.UNC) handleUpdateProductRoot(ProductField.DeliveryStorageId, null);
              }}
              onReset={(v) => {
                // Non executed... strange, must use onChange
                handleUpdateProductRoot(ProductField.DeliveryMethod, null);
                handleUpdateProductRoot(ProductField.DeliveryStorageId, null);
              }}
              getOptionLabel={(value: any) =>
                value === DeliveryMethod.AsperaFaspex
                  ? t("form:statusText.asperaDownloadLink")
                  : value === DeliveryMethod.Http
                  ? "HTTP"
                  : value === DeliveryMethod.MailAttachment
                  ? t("field:option.mailAttachment")
                  : t("form:statusText.transfer")
              }
              isOptionEqualToValue={(option, value) => option === value}
              filterOptions={(x, s) => x.filter((a) => a.toString().toLowerCase().includes(s.inputValue.toLowerCase()))}
              validationProps={{
                formId: formid,
                formSectionId: "sectionDelivery",
                fieldId: "deliveryMode",
                required: true,
                initialState: false,
              }}
            />
          </Grid2>
          <Grid2 size={7}>
            <FormAutocomplete
              label={t("field:label.destinationStorage")}
              options={storages.map((s) => s.id)}
              value={item.selectedProduct!.deliveryStorageId}
              multiple={false}
              onChange={(v) => {
                handleUpdateProductRoot(ProductField.DeliveryStorageId, v);
              }}
              onReset={(v) => {
                // Non executed... strange, must use onChange
                handleUpdateProductRoot(ProductField.DeliveryStorageId, null);
              }}
              getOptionLabel={(value: any) => storages.find((x) => x.id === value)?.name!}
              isOptionEqualToValue={(option, value) => option === value}
              filterOptions={(x, s) => {
                let match = storages.filter((x) => x.name!.toLowerCase().includes(s.inputValue.toLowerCase()));
                return x.filter((a) => match.map((s) => s.id).includes(a));
              }}
              validationProps={{
                formId: formid,
                formSectionId: "sectionDelivery",
                fieldId: "deliveryStorage",
                required: item.selectedProduct?.deliveryMethod === DeliveryMethod.UNC,
                initialState: false,
              }}
              stackProps={{
                sx: {
                  display:
                    !item.selectedProduct?.deliveryMethod ||
                    item.selectedProduct?.deliveryMethod === DeliveryMethod.AsperaFaspex ||
                    item.selectedProduct?.deliveryMethod === DeliveryMethod.Http ||
                    item.selectedProduct?.deliveryMethod === DeliveryMethod.MailAttachment
                      ? "none"
                      : "",
                },
              }}
            />
          </Grid2>
        </Grid2>

        <Divider sx={{ mt: 2, mb: 2 }} />

        {maillists && maillists.length > 0 && (
          <FormAutocomplete
            label={t("field:label.loadEmailsFromMailingList")}
            options={maillists}
            placeholder={t("field:placeholder.searchMailingList")}
            multiple={false}
            value={ml}
            onChange={(v) => {
              /*let mails =
                        item.selectedProduct!.emailNotificationList !== null
                          ? item.selectedProduct!.emailNotificationList
                          : [];
            
                      v.emails.forEach((mail) => {
                        mails?.push(mail);
                      });*/

              if (v !== null) handleUpdateProductRoot(ProductField.Emails, v.emails);

              setMl(undefined);
            }}
            onReset={() => setMl(undefined)}
            getOptionLabel={(value: any) => value.name}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            validationProps={{
              formId: formid,
              formSectionId: "sectionDelivery",
              fieldId: "deliveryEmailsList",
              required: false,
              initialState: false,
            }}
            stackProps={{ sx: { mb: 2 } }}
          />
        )}

        <Stack>
          <InputLabel sx={{ mb: 0 }}>Search emails into platform users list</InputLabel>
          <Autocomplete
            options={fetchedUsersEmails}
            onInputChange={(e, value) => handleAutocompleteUsersMailInputChange(value)}
            noOptionsText={t("field:helper.typeNameToFetchEmails")}
            inputValue={query}
            value={null}
            onChange={(e, value) => {
              handleUpdateProductRoot(ProductField.Emails, [value!.split(" ")[0]]);
            }}
            renderInput={(params) => (
              <TextField sx={{ mt: -2, mb: 2 }} {...params} placeholder={t("field:placeholder.searchMailingList")} />
            )}
          />
        </Stack>

        {recentEmails.length > 0 && (
          <Stack mb={2}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.useRecentEmailList")}</InputLabel>
            <Stack direction="row" alignContent="center" alignItems="center" justifyContent="space-between">
              <Button size="small" onClick={() => handleUpdateProductRoot(ProductField.Emails, recentEmails)}>
                {t("common:takeEmails", { count: recentEmails.length })}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {recentEmails.join(", ")}
              </Typography>
            </Stack>
          </Stack>
        )}

        <FormAutocomplete
          label={t("field:label.emailContacts")}
          options={[]}
          freeSolo
          placeholder={t("field:placeholder.email")}
          value={item.selectedProduct!.emailNotificationList}
          multiple={true}
          onChange={(v) => {
            if (v.length > 0) {
              let lastPart = v[v.length - 1];
              let listDelimiter = IsListOfEmails(lastPart);

              if (listDelimiter !== null) {
                let emails: string[] = lastPart.split(listDelimiter);
                emails = emails.map((x) => x.trim());

                for (let i = 0; i < emails.length; i++) {
                  if (!IsEmail(emails[i])) return;
                }

                handleUpdateProductRoot(ProductField.Emails, emails);
              } else {
                if (!IsEmail(lastPart)) return;
                else handleUpdateProductRoot(ProductField.Emails, v);
              }
            } else handleUpdateProductRoot(ProductField.Emails, v);
          }}
          onReset={(v) => {
            // Non executed... strange, must use onChange
            handleUpdateProductRoot(ProductField.Emails, []);
          }}
          getOptionLabel={(value: any) => value}
          isOptionEqualToValue={(option, value) => option === value}
          validationProps={{
            formId: formid,
            formSectionId: "sectionDelivery",
            fieldId: "deliveryEmails",
            required: true,
            initialState: false,
          }}
          inputName="delivery_emails"
        />

        <Divider sx={{ mt: 2 }} />

        <FormInputText
          label={t("common:comment")}
          rows={4}
          name="delivery_comment"
          stackProps={{ mt: 2 }}
          multiline
          placeholder={t("field:placeholder.deliveryComment")}
          value={!item.selectedProduct!.comment ? "" : item.selectedProduct!.comment}
          onChange={(v) => handleUpdateProductRoot(ProductField.Comment, v.target.value)}
          validationProps={{
            formId: formid,
            formSectionId: "sectionDelivery",
            fieldId: "deliveryComment",
            initialState: false,
            required: false,
            minLength: 0,
          }}
        />

        {/* Here I have to look for my other files */}
        {item.selectedProduct!.deliveryMethod !== DeliveryMethod.Http && files.length > 0 && (
          <>
            <Divider sx={{ mt: 2, mb: 2 }} />

            <Typography>{t("component:config.body.deliverSidecarFiles")}</Typography>
            <DataTable
              noControls
              sx={{ mt: 1 }}
              rows={files}
              columnHeaderHeight={48}
              rowHeight={56}
              getRowHeight={() => "auto"}
              pagination={false}
              disableRowSelectionOnClick
              columns={columns}
              hideFooter={true}
              checkboxSelection
              selectedRows={item.selectedProduct?.transferMediaAnnexFileIds ?? undefined}
              setSelectedRows={setSelectedFiles}
            />
          </>
        )}
      </FormSection>
      {/* Here I can add DCP OV to my VF */}
      {dcpOriginalVersionPackageId && (
        <Stack
          direction="row"
          gap={1}
          sx={(t) => ({ background: t.palette.info.alpha, borderRadius: 1, p: 1, pl: 1.5 })}
        >
          <Stack flex={"1 1 0%"}>
            <Typography variant="body1" fontWeight={500} color="info.text">
              {t("page:configAddDCPOV.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("page:configAddDCPOV.description")}
            </Typography>
          </Stack>
          <Button size="small" onClick={() => addOriginalDcpVersionPackage()}>
            {t("page:configAddDCPOV.button")}
          </Button>
        </Stack>
      )}
    </>
  );
};

export default ConfigCommonSectionFormDelivery;
